import type { ChooseProduct, FormData } from "@ifa/utils";

export type ZuuFnaObjective = {
  key: string;
  title: string;
  details: string[];
};

export type ZuuFnaProduct = {
  id: string;
  name: string;
  company: string;
  paymentPeriod: string;
  coveragePeriod: string;
};

const OBJECTIVE_LABELS: Record<string, string> = {
  A: "為應付不時之需提供財務保障",
  B: "為醫療保健需要作準備",
  C: "為未來提供定期收入",
  D: "為未來需要做儲蓄",
  E: "投資",
  F: "其他",
};

const PAYMENT_PERIOD_LABELS: Record<string, string> = {
  A: "2-5年",
  B: "6-10年",
  C: "11-15年",
  D: "16-20年",
  E: "超過20年",
  F: "終身",
  G: "一次性付款不超過",
};

const COVERAGE_PERIOD_LABELS: Record<string, string> = {
  A: "少於1年",
  B: "1-5年",
  C: "6-10年",
  D: "11-15年",
  E: "16-20年",
  F: "超過20年",
  G: "終身",
};

function findFormData(financialAnalysis: FormData[], question: string) {
  return financialAnalysis.find((item) => item.question === question);
}

function getInputValue(financialAnalysis: FormData[], question: string) {
  const rawValue = findFormData(financialAnalysis, question)?.items?.find(
    (item) => typeof item.value === "string" || typeof item.value === "number",
  )?.value;

  return typeof rawValue === "string" || typeof rawValue === "number"
    ? String(rawValue)
    : "";
}

function getSelectedKeys(financialAnalysis: FormData[], question: string) {
  return (
    findFormData(financialAnalysis, question)?.items
      ?.filter((item) => item.value === true)
      .map((item) => item.key)
      .filter((item): item is string => Boolean(item)) ?? []
  );
}

function getSelectedKey(financialAnalysis: FormData[], question: string) {
  return getSelectedKeys(financialAnalysis, question)[0] ?? "";
}

function formatCurrency(value: string) {
  if (!value) return "--";

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return `${numeric.toLocaleString()} HKD`;
  }

  return value;
}

export function formatZuuFnaPaymentPeriod(financialAnalysis: FormData[] = []) {
  const key = getSelectedKey(financialAnalysis, "willingPremiumPaymentPeriodRange");
  if (!key) return "待完善";

  const label = PAYMENT_PERIOD_LABELS[key];
  if (!label) return "待完善";

  if (key === "G") {
    const amount = getInputValue(financialAnalysis, "singlePremiumPaymentLimit");
    return amount ? `${label} ${amount}HKD` : label;
  }

  return label;
}

export function formatZuuFnaCoveragePeriod(financialAnalysis: FormData[] = []) {
  const key = getSelectedKey(financialAnalysis, "policyTargetExpectedTime");
  return key ? COVERAGE_PERIOD_LABELS[key] ?? "待完善" : "待完善";
}

function buildObjectiveDetails(financialAnalysis: FormData[], key: string) {
  if (key === "A") {
    return [
      `額外人壽保障額：${formatCurrency(
        getInputValue(financialAnalysis, "additionalLifeCoverageAmount"),
      )}`,
    ];
  }

  if (key === "B") {
    return [
      `額外危疾保障額：${formatCurrency(
        getInputValue(financialAnalysis, "additionalCriticalIllnessCoverageAmount"),
      )}`,
    ];
  }

  if (key === "C" || key === "D") {
    return [
      `目標年期：${
        getInputValue(financialAnalysis, "additionalSavingsTargetReturnPeriod") || "--"
      }`,
      `目標金額：${formatCurrency(
        getInputValue(financialAnalysis, "additionalSavingsTargetReturnAmount"),
      )}`,
    ];
  }

  if (key === "E") {
    return [
      `投資年期：${
        getInputValue(financialAnalysis, "additionalInvestmentTargetReturnPeriod") || "--"
      }`,
      `投資回報：${formatCurrency(
        getInputValue(financialAnalysis, "additionalInvestmentTargetReturnAmount"),
      )}`,
    ];
  }

  if (key === "F") {
    return [
      `其他說明：${
        getInputValue(financialAnalysis, "insurancePurchaseOtherObjectives") || "--"
      }`,
    ];
  }

  return [];
}

function mapProduct(
  product: ChooseProduct,
  index: number,
  paymentPeriod: string,
  coveragePeriod: string,
): ZuuFnaProduct {
  return {
    id: product.productCode || product.productName || `zuu-product-${index}`,
    name: product.productName || product.productCode || `產品 ${index + 1}`,
    company: product.companyName || product.companyCode || "待完善",
    paymentPeriod,
    coveragePeriod,
  };
}

export function buildZuuFnaView(
  financialAnalysis: FormData[] = [],
  chooseProducts: ChooseProduct[] = [],
) {
  const coveragePeriod = formatZuuFnaCoveragePeriod(financialAnalysis);
  const paymentPeriod = formatZuuFnaPaymentPeriod(financialAnalysis);

  const objectives: ZuuFnaObjective[] = getSelectedKeys(
    financialAnalysis,
    "insurancePurchaseObjectives",
  ).map((key) => ({
    key,
    title: OBJECTIVE_LABELS[key] ?? key,
    details: buildObjectiveDetails(financialAnalysis, key),
  }));

  const introducedProducts = chooseProducts.map((product, index) =>
    mapProduct(product, index, paymentPeriod, coveragePeriod),
  );

  const selectedProducts = chooseProducts
    .filter((product) => product.choose === true)
    .map((product, index) => mapProduct(product, index, paymentPeriod, coveragePeriod));

  return {
    objectives,
    coveragePeriod,
    paymentPeriod,
    introducedProducts,
    selectedProducts,
  };
}
